import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import PortalLayout from "@/components/PortalLayout";
import api from "@/lib/api";
import { FileText, Download } from "lucide-react";

const statusStyle = {
  paid:   { bg: "#D1FAE5", text: "#059669", label: "Paid" },
  sent:   { bg: "#DBEAFE", text: "#1D4ED8", label: "Sent" },
  draft:  { bg: "#F5F3EF", text: "#78716C", label: "Draft" },
  overdue:{ bg: "#FEE2E2", text: "#DC2626", label: "Overdue" },
};

function getStatus(inv) {
  if (inv.status === "paid") return "paid";
  if (inv.status === "sent" && inv.due_date && inv.due_date < new Date().toISOString().slice(0, 10)) return "overdue";
  return inv.status || "sent";
}

export default function PortalInvoices() {
  const { invoiceId } = useParams();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const API = process.env.REACT_APP_BACKEND_URL;
  const highlightRef = useRef(null);

  useEffect(() => {
    api.get("/portal/invoices").then((r) => setInvoices(r.data)).finally(() => setLoading(false));
  }, []);

  // Auto-scroll to highlighted invoice when data loads
  useEffect(() => {
    if (invoiceId && !loading && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [invoiceId, loading]);

  return (
    <PortalLayout>
      <div data-testid="portal-invoices-page">
        <h1 className="font-serif text-3xl font-bold mb-2" style={{ color: "#292524" }}>Invoices</h1>
        <p className="text-sm mb-8" style={{ color: "#57534E" }}>Your billing history</p>

        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: "#E7E5E4" }}>
          {loading ? (
            <div className="py-12 text-center text-sm" style={{ color: "#A8A29E" }}>Loading...</div>
          ) : invoices.length === 0 ? (
            <div className="py-12 text-center">
              <FileText size={28} className="mx-auto mb-3" style={{ color: "#E7E5E4" }} />
              <p className="text-sm" style={{ color: "#A8A29E" }}>No invoices yet</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: "1px solid #E7E5E4" }}>
                  <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Invoice</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Due</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Amount</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const s = statusStyle[getStatus(inv)] || statusStyle.sent;
                  const isHighlighted = inv.id === invoiceId;
                  return (
                    <tr
                      key={inv.id}
                      ref={isHighlighted ? highlightRef : null}
                      data-testid={`invoice-row-${inv.id}`}
                      className="border-b transition-colors"
                      style={{
                        borderColor: "#F5F3EF",
                        background: isHighlighted ? "#FEF3C7" : undefined,
                      }}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <FileText size={14} style={{ color: "#A8A29E" }} />
                          <span className="text-sm font-medium" style={{ color: "#292524" }}>{inv.invoice_number}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm" style={{ color: "#57534E" }}>{inv.created_at?.slice(0, 10)}</td>
                      <td className="px-4 py-4 text-sm" style={{ color: "#57534E" }}>{inv.due_date || "—"}</td>
                      <td className="px-4 py-4 text-sm font-medium" style={{ color: "#292524" }}>
                        £{(inv.total || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: s.bg, color: s.text }}>
                          {s.label}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <a
                          href={`${API}/api/invoices/${inv.id}/pdf`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`download-invoice-${inv.id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors hover:bg-stone-50"
                          style={{ borderColor: "#E7E5E4", color: "#57534E" }}
                        >
                          <Download size={12} />
                          PDF
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </PortalLayout>
  );
}
